/* eslint-disable unicorn/filename-case */
import React, { useEffect, useState, useContext, useCallback } from 'react';
import _ from 'lodash';
import { Redirect, withRouter } from 'react-router-dom';
import { useModal, Loader } from '@jutro/components';
import PropTypes from 'prop-types';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
 
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { PolicyChange, messages as commonMessages } from 'gw-capability-policychange-common-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { CodegenContext } from 'gw-capability-quoteandbind-PremierPL-react';
 
import messages from './PremierSLPolicyChange.messages';
import wizardConfig from './config/PremierSL-policychange-wizard-config.json5';


function PremierSLPolicyChangeWizard(props) {
    const {
        showAlert,
	    showConfirm
    } = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const { EndorsementService } = useDependencies('EndorsementService');

    useEffect(
        () => {
            if (!location.state) {
                history.push('/');
                
                return;
            }

            const {
                state: { policyNumber }
            } = location;

            if (!policyNumber) {
                return;
            }

            const errorModalBox = (errorMessage) => {
                showConfirm(errorMessage).then((results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }

                    setIsLoading(false);

                    return history.push(`/contactAgent/${policyNumber}`);
                }, _.noop);
            };

            EndorsementService.retrieveEndorsement(policyNumber, authHeader)
                    .then((responseData) => {
                        const submission = viewModelService.create(
                            new PolicyChange(responseData),
                            'pc',
                            'edge.capabilities.policychange.dto.PolicyChangeDataDTO'
                        );

                        setInitialSubmission(submission);
                        setIsLoading(false);
                    })
                    .catch(() => {
                        errorModalBox({
                            title: commonMessages.unableToLoadDraftPolicy,
                            message: commonMessages.somethingWentWrong,
                            status: 'warning',
                            icon: 'mi-error-outline'
                        });
                    });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleOnCancel = useCallback(
        ({ wizardSnapshot, wizardData, param: nextPath = '' }) => {
            const skipModal = ['contactAgent'];
            const lastPathName = nextPath.replace(/\/?.*\//g, '');
            const isBound = _.get(wizardData.value, 'policyChange.status') === 'Bound';

            if (isBound || nextPath.startsWith('/accounts') || nextPath.startsWith('/policies/') || nextPath.startsWith('/change') || skipModal.includes(lastPathName) || hasErrorOccurred) {
                return true;
            }

            return showConfirm({
                title: messages.wantToCancel,
                message: messages.infoWillBeSavedAsDraft,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }

                setIsLoading(true);

                const { jobID } = wizardData.value;
                const notQuoted = _.get(wizardData.value, 'status') !== 'Quoted';
                const redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/change/${jobID}/summary`;

                if (notQuoted) {
                    EndorsementService.saveEndorsement([wizardSnapshot.value], authHeader)
                        .then(() => history.push(redirectLobPath))
                        .catch(() => {
                            showConfirm({
                                title: commonMessages.unableToDraftPolicy,
                                message: commonMessages.anErrorOccurred,
                                status: 'warning',
                                icon: 'mi-error-outline',
                            });
                        })
                        .finally(() => {
                            setIsLoading(false);
                        });

                    return true;
                }

                return history.push(redirectLobPath);
            }, _.noop);
        },
        [EndorsementService, authHeader, hasErrorOccurred, history, showConfirm]
    );

    const getCodeGenContext = () => {
        const lobData = _.get(initialSubmission, 'lobData.value');
        const firstAvailableLob = lobData && Object.keys(lobData)[0];

        return {
            wizardConfig,
            lineName: firstAvailableLob,
            lobName: 'ConsTraAndServProduct',
            productCode: 'ConsTraAndServProduct'
        };
    };
        
    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const jobIDFromWizard = _.get(error, 'gwInfo.params[0].jobID');
            const jobIDFromLocation = _.get(location, 'state.policyNumber');
            let redirectPath = `/contactAgent/${jobIDFromLocation}`;

            if (!_.isEmpty(jobIDFromWizard)) {
                redirectPath = `/change/${jobIDFromWizard}/summary`;
            }

            const state = {
                pathname: redirectPath,
                state: {
                    redirectPath: '/home',
                }
            };

            return showAlert({
                title: isQuotePage ? messages.saveQuoteError : messages.anErrorOccurredTitle,
                message: isQuotePage ? messages.saveQuoteErrorMessage : messages.anErrorOccurred,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: platformMessages.ok
            }).then(() => {
                setHasErrorOccurred(true);

                return <Redirect to={state} />;
            }, _.noop);
        },
        [location, showAlert]
    );

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }    

    if (!initialSubmission) {
        return null;
    }

    const isStatusQuoted = _.get(initialSubmission, 'baseData.periodStatus.value.code') === 'Quoted';

    return (
        <div>
            <ErrorBoundary onError={handleError}>
                <CodegenContext.Provider value={getCodeGenContext()}>
                    <Wizard
                        initialSteps={steps}
                        wizardTitle={title}
                        initialData={ initialSubmission }
                        onCancel={handleOnCancel}
                        skipCompletedSteps={isStatusQuoted}
                        // wizardStepToFieldMapping={wizardStepToFieldMapping}
                        onPreviousModalProps={{
                            title: platformMessages.wantToJump,
                            message: platformMessages.wantToJumpMessage,
                            status: 'warning',
                            icon: 'mi-error-outline',
                            confirmButtonText: platformMessages.yes,
                            cancelButtonText: platformMessages.no
                        }}
                        extractVMObject={(data) => _.get(data, 'submissionVM')}
                    />
                </CodegenContext.Provider>                
            </ErrorBoundary>
        </div>
    );
}

PremierSLPolicyChangeWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            requestType: PropTypes.string,
            policyDetails: PropTypes.shape({
                effective: PropTypes.instanceOf(Date)
            }),
            isFromPolicySummary: PropTypes.bool
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(withViewModelService(PremierSLPolicyChangeWizard));